import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { openChart, ChartView } from '../components/ChartWindow';
import useMyPositions from '../components/useMyPositions';
import { wsUrl } from '../services/urls';
import PriceChart, { TIMEFRAMES } from '../components/PriceChart';
import './Watchlist.css';
import OrderConfirm from '../components/OrderConfirm';
import PendingOrders from '../components/PendingOrders';

function Watchlist() {
  const [watchlist, setWatchlist] = useState([]);
  const [prices, setPrices] = useState({});
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeView, setActiveView] = useState('list');
  const [optionChain, setOptionChain] = useState([]);
  const [chainExpiry, setChainExpiry] = useState('');
  const [chainIsReal, setChainIsReal] = useState(true);
  const [underlyingPrice, setUnderlyingPrice] = useState(0);
  const [loadingChain, setLoadingChain] = useState(false);
  const [lots, setLots] = useState(1);
  const [exchange, setExchange] = useState('NSE');
  const [chartData, setChartData] = useState([]);
  const [bigPlayerTf, setBigPlayerTf] = useState(null);     // big-player footprint, chart's timeframe
  const [bigPlayerMtf, setBigPlayerMtf] = useState(null);   // 5m / 15m / 1h / Day summary
  const [sellerWalls, setSellerWalls] = useState(null);     // option-seller call/put walls + stop-loss status
  const [chartError, setChartError] = useState('');
  const [loadingChart, setLoadingChart] = useState(false);
  const [message, setMessage] = useState(null);
  const [indices, setIndices] = useState([]);
  const [mcxCommodities, setMcxCommodities] = useState([]);
  const [foStocks, setFoStocks] = useState([]);
  const [technicals, setTechnicals] = useState({});
  const [marketOpen, setMarketOpen] = useState(null); // null = unknown yet
  const [chartTimeframe, setChartTimeframe] = useState('5m');
  const [mcxFutures, setMcxFutures] = useState([]);
  const [mcxOptionExpiries, setMcxOptionExpiries] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState('');
  // AI PICKS (owner 2026-10-15): stock-option signals that passed every check — the user places them.
  // Keeps the last good list on a failed poll (no flicker).
  const [aiPicks, setAiPicks] = useState([]);
  const [chartAi, setChartAi] = useState(null);   // AI BUY CE / PE / EXIT marks for the open chart
  const my = useMyPositions();
  const [picksAuto, setPicksAuto] = useState(true);
  const [pickLots, setPickLots] = useState(1);
  useEffect(() => {
    let alive = true;
    const load = async () => { try { const r = await api.getAiPicks(); if (alive && r && r.status === 'success') { setAiPicks(r.picks || []); setPicksAuto(!!r.auto); } } catch (e) { /* keep last */ } };
    load(); const iv = setInterval(load, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  const debounceRef = useRef(null);
  const wsRef = useRef(null);

  const INDEX_SYMBOLS = new Set(['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'NIFTYNXT50', 'SENSEX', 'BANKEX']);
  const inferSegment = (item) => {
    if (item.segment) return item.segment;
    if ((item.exchange || '').toUpperCase() === 'MCX') return 'commodity';
    if (INDEX_SYMBOLS.has((item.symbol || '').toUpperCase())) return 'index';
    return 'equity';
  };
  const segmentLabel = { equity: 'Equity', index: 'Index', commodity: 'Commodity', fo: 'F&O Stock', options: 'Option', futures: 'Future' };
  const segmentColor = { equity: '#00d9ff', index: '#ffb020', commodity: '#ff8844', fo: '#00ff88', options: '#a855f7', futures: '#f472b6' };

  const showMessage = (type, text, ms = 3000) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), ms);
  };

  // PER-USER watchlist: keyed by the logged-in user id so each client keeps
  // their own list — previously one shared 'watchlist' key showed the admin's
  // list to every user who logged in on the same browser.
  const wlUser = (() => {
    try { return JSON.parse(localStorage.getItem('app_user') || '{}'); } catch { return {}; }
  })();
  const WL_KEY = `watchlist_${wlUser.user_id || 'guest'}`;

  // Load watchlist from localStorage. The save-effect below is gated on
  // `watchlistLoaded` — without that gate, React runs BOTH effects on mount with
  // the still-empty initial state, so the save-effect fires first and overwrites
  // your saved watchlist with [] a moment before the load actually lands. That
  // was silently wiping the watchlist on every page refresh.
  const [watchlistLoaded, setWatchlistLoaded] = useState(false);
  useEffect(() => {
    let saved = localStorage.getItem(WL_KEY);
    // one-time migration: the old shared key becomes the ADMIN's list only
    if (!saved && wlUser.role === 'admin') {
      saved = localStorage.getItem('watchlist');
      if (saved) localStorage.setItem(WL_KEY, saved);
    }
    if (saved) {
      try { setWatchlist(JSON.parse(saved)); } catch (e) {}
    }
    setWatchlistLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save watchlist to localStorage — only once loading has completed
  useEffect(() => {
    if (!watchlistLoaded) return;
    localStorage.setItem(WL_KEY, JSON.stringify(watchlist));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlist, watchlistLoaded]);

  // Stay in sync if the watchlist is changed in another browser tab — otherwise
  // this tab's stale copy can later save over the other tab's newer changes.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === WL_KEY && e.newValue) {
        try { setWatchlist(JSON.parse(e.newValue)); } catch (err) {}
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Loaded once up front so autosuggest can match against indices/commodities/F&O
  // stocks instantly (client-side) without a round trip on every keystroke.
  useEffect(() => {
    api.getIndices().then(res => setIndices(res.indices || [])).catch(() => {});
    api.getMcxCommodities().then(res => setMcxCommodities(res.commodities || [])).catch(() => {});
    api.getFoStocks().then(res => setFoStocks(res.stocks || [])).catch(() => {});
  }, []);

  // WebSocket for real-time prices (covers symbols the engine holds open positions in)
  useEffect(() => {
    const ws = new WebSocket(wsUrl('/ws/prices'));
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === 'prices' && msg.data) {
        setPrices(prev => ({ ...prev, ...msg.data }));
      }
    };
    ws.onerror = () => {};
    wsRef.current = ws;
    return () => ws.close();
  }, []);

  // Poll real LTP/OHLC for every watchlist symbol
  useEffect(() => {
    if (watchlist.length === 0) return;

    const fetchWatchlistPrices = async () => {
      try {
        const res = await api.getWatchlistPrices(watchlist);
        if (res.status === 'success' && res.prices) {
          // never let a transient zero overwrite a good price — that's what made
          // prices "go and come" (flicker to — and back) on the screen
          setPrices(prev => {
            const merged = { ...prev };
            Object.entries(res.prices).forEach(([k, v]) => {
              if (v && v.ltp > 0) merged[k] = v;
              else if (!(k in merged)) merged[k] = v; // first load: show the row even if empty
            });
            return merged;
          });
        }
      } catch (e) {}
    };

    fetchWatchlistPrices();
    const interval = setInterval(fetchWatchlistPrices, 2000);
    return () => clearInterval(interval);
  }, [watchlist]);

  // Technicals (RSI/trend/AI signal) — cheaper to poll slowly; backend caches 60s anyway
  useEffect(() => {
    if (watchlist.length === 0) return;

    const fetchTechnicals = async () => {
      try {
        const res = await api.getWatchlistTechnicals(watchlist);
        if (res.status === 'success' && res.technicals) {
          setTechnicals(prev => ({ ...prev, ...res.technicals }));
        }
      } catch (e) {}
    };

    fetchTechnicals();
    const interval = setInterval(fetchTechnicals, 15000);
    return () => clearInterval(interval);
  }, [watchlist]);

  // Auto-refresh the option chain while it's open — premiums move fast
  useEffect(() => {
    if (activeView !== 'options' || !selectedItem) return;
    const interval = setInterval(() => {
      loadOptionChain(selectedItem.symbol, selectedItem.exchange, selectedExpiry);
    }, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [activeView, selectedItem, selectedExpiry]);

  // Market-open status for whichever symbol's chart is showing — polled every
  // 30s, cheap enough and catches the open/close transition promptly.
  useEffect(() => {
    if (activeView !== 'chart' || !selectedItem) { setMarketOpen(null); return; }
    const checkStatus = async () => {
      try {
        const res = await api.getMarketStatus(selectedItem.exchange);
        setMarketOpen(!!res.is_open);
      } catch (e) {}
    };
    checkStatus();
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, [activeView, selectedItem]);

  // Live chart — while the market is open, silently refetch real candles every
  // 10s so the last (forming) bar updates as price moves, instead of the chart
  // being a one-time snapshot from whenever you clicked "Chart". Daily/weekly
  // bars don't need second-by-second polling, so auto-refresh skips those.
  useEffect(() => {
    if (activeView !== 'chart' || !selectedItem || marketOpen !== true) return;
    if (selectedItem.exchange.toUpperCase() === 'MCX') return; // no chart source for MCX yet
    if (chartTimeframe === '1d' || chartTimeframe === '1wk') return;
    const interval = setInterval(() => {
      loadChart(selectedItem.symbol, selectedItem.exchange, true);
    }, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [activeView, selectedItem, marketOpen, chartTimeframe]);

  // Auto-suggest on type — merges live backend search (equity/stock symbols) with
  // local matches from indices, MCX commodities, and F&O stocks, so typing ANY
  // one word finds it regardless of segment. No tabs, no pre-browsing required.
  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const qUpper = q.toUpperCase();
    const merged = [];
    const seen = new Set();
    const push = (symbol, exch, segment, name) => {
      const key = `${symbol}:${exch}`;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push({ symbol, tradingsymbol: symbol, exchange: exch, segment, name });
    };

    indices.forEach(i => {
      if (i.symbol.includes(qUpper) || (i.name || '').toUpperCase().includes(qUpper)) push(i.symbol, i.exchange, 'index', i.name);
    });
    mcxCommodities.forEach(c => {
      if (c.symbol.includes(qUpper)) push(c.symbol, c.exchange, 'commodity', c.next_expiry ? `Exp: ${c.next_expiry}` : undefined);
    });
    foStocks.forEach(s => {
      if (s.symbol.includes(qUpper)) push(s.symbol, s.exchange, 'fo');
    });

    try {
      const res = await api.searchSymbols(q, exchange, '', 12);
      // The backend returns the company's long display name in `.symbol` (e.g. "STATE
      // BANK OF INDIA") and the real ticker in `.tradingsymbol` (e.g. "SBIN") — the
      // ticker is what price lookups and option-chain matching actually need.
      // `kind` tags derivative contracts (option/future) so they get the right
      // segment badge and price routing (NFO) when added to the watchlist.
      (res.symbols || []).forEach(s => {
        const seg = s.kind === 'option' ? 'options'
          : s.kind === 'future' ? 'futures'
          : (exchange === 'MCX' ? 'commodity' : 'equity');
        push(s.tradingsymbol || s.symbol, s.exchange, seg, s.symbol);
      });
    } catch (e) {}

    setSuggestions(merged.slice(0, 15));
    setShowSuggestions(true);
  }, [exchange, indices, mcxCommodities, foStocks]);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 200);
  };

  const addToWatchlist = (sym, segment) => {
    if (watchlist.find(w => w.symbol === sym.symbol && w.exchange === sym.exchange)) {
      showMessage('error', `${sym.symbol} already in watchlist`);
      return;
    }
    const newItem = {
      symbol: sym.symbol,
      tradingsymbol: sym.tradingsymbol || sym.symbol,
      exchange: sym.exchange,
      segment: segment || inferSegment(sym),
      addedAt: Date.now()
    };
    setWatchlist(prev => [...prev, newItem]);
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    showMessage('success', `Added ${sym.symbol}`);
  };

  const removeFromWatchlist = (symbol, exchange) => {
    setWatchlist(prev => prev.filter(w => !(w.symbol === symbol && w.exchange === exchange)));
    if (selectedItem?.symbol === symbol && selectedItem?.exchange === exchange) {
      setSelectedItem(null);
      setActiveView('list');
    }
  };

  const loadOptionChain = async (sym, ex, expiry = '') => {
    setLoadingChain(true);
    setSelectedItem({ symbol: sym, exchange: ex });
    setActiveView('options');
    const isMcx = ex.toUpperCase() === 'MCX';
    try {
      let res;
      if (isMcx) {
        res = await api.fyersMcxOptions(sym, expiry);
        // Real futures (with real expiry) and real option expiries — MCX-only,
        // fetched alongside the option chain so both are visible together.
        api.getMcxFutures(sym).then(f => setMcxFutures(f.futures || [])).catch(() => setMcxFutures([]));
        api.getMcxExpiries(sym).then(e => setMcxOptionExpiries(e.options_expiries || [])).catch(() => setMcxOptionExpiries([]));
      } else {
        res = await api.getOptionChain(sym, ex, expiry);
        setMcxFutures([]);
        setMcxOptionExpiries([]);
      }
      if (res.status === 'success') {
        setOptionChain(res.options || []);
        setUnderlyingPrice(res.underlying_price || 0);
        const resolvedExpiry = res.chain_expiry || res.options?.[0]?.expiry || '';
        setChainExpiry(resolvedExpiry);
        setSelectedExpiry(resolvedExpiry);
        setChainIsReal(isMcx ? (res.real !== false) : (res.real !== false));
      } else {
        setOptionChain([]);
        showMessage('error', res.message || 'No options');
      }
    } catch (e) {
      setOptionChain([]);
    }
    setLoadingChain(false);
  };

  const handleExpiryChange = (expiry) => {
    setSelectedExpiry(expiry);
    if (selectedItem) loadOptionChain(selectedItem.symbol, selectedItem.exchange, expiry);
  };

  const loadChart = async (sym, ex, silent = false) => {
    // `silent` = background auto-refresh while the market's open: keep the existing
    // candles on screen and just swap them once the new ones land, instead of
    // flashing "Loading..." and blanking the chart every 10 seconds.
    if (!silent) {
      setSelectedItem({ symbol: sym, exchange: ex });
      setActiveView('chart');
      setChartError('');
      setChartData([]);
      if (ex.toUpperCase() === 'MCX') {
        setChartError('Real-time charting is not yet available for MCX commodities.');
        return;
      }
      setLoadingChart(true);
    }
    try {
      if (!silent) {
        setBigPlayerMtf(null);
        api.getBigPlayer(sym).then(r => { if (r && r.status === 'success') setBigPlayerMtf(r); }).catch(() => {});
      }
      const res = await api.getCandles(sym, ex, '', chartTimeframe);
      if (res.status === 'success' && res.candles?.length) {
        setChartData(res.candles);
        if (res.big_player) setBigPlayerTf(res.big_player);
        if (res.seller !== undefined) setSellerWalls(res.seller);
        if (silent) setChartError('');
      } else if (!silent) {
        setChartError(res.message || 'No chart data available');
      }
    } catch (e) {
      if (!silent) setChartError('Failed to load chart');
    }
    if (!silent) setLoadingChart(false);
  };

  const handleTimeframeChange = (tf) => {
    setChartTimeframe(tf);
    if (!selectedItem) return;
    // Fetch with `tf` directly rather than relying on the chartTimeframe state
    // (which won't have committed yet within this same event handler).
    setChartError('');
    setChartData([]);
    if (selectedItem.exchange.toUpperCase() === 'MCX') {
      setChartError('Real-time charting is not yet available for MCX commodities.');
      return;
    }
    setLoadingChart(true);
    api.getCandles(selectedItem.symbol, selectedItem.exchange, '', tf)
      .then(res => {
        if (res.status === 'success' && res.candles?.length) { setChartData(res.candles); setBigPlayerTf(res.big_player || null); setSellerWalls(res.seller || null); }
        else setChartError(res.message || 'No chart data available');
      })
      .catch(() => setChartError('Failed to load chart'))
      .finally(() => setLoadingChart(false));
  };

  // MANUAL ORDER -> the user's OWN book (paper balance, or their own connected broker on
  // live). Old path sent MARKET orders through a shared mStock / the admin's data-only Fyers
  // and never tracked the position. Now it opens a tracked position like an auto trade.
  // in-page confirm (browser pop-ups were silently blocked on some phones -> no order ever sent)
  const [orderAsk, setOrderAsk] = useState(null);
  const placeManual = (symbol, exchange, side, nLots, lotSize, label, price) => {
    setOrderAsk({ symbol, exchange, side, qty: Math.max(1, nLots || 1), lotSize: Math.max(1, lotSize || 1), label,
      price: price > 0 ? +(+price).toFixed(2) : undefined });   // pre-fills the limit price box
  };

  useEffect(() => {
    if (!selectedItem || activeView !== 'chart') return undefined;
    let alive = true;
    setChartAi(null);
    const q = async () => { try { const r = await api.getChartAi(selectedItem.symbol); if (alive && r.status === 'success') setChartAi(r); } catch (e) { /* keep last */ } };
    q(); const iv = setInterval(q, 60000); return () => { alive = false; clearInterval(iv); };
  }, [selectedItem && selectedItem.symbol, activeView]);   // eslint-disable-line react-hooks/exhaustive-deps

  // REMEMBER WHAT WAS OPEN (owner 2026-10-16: "it should stay on that page"): the selected symbol and view
  // (chart / option chain / technicals) come back after a refresh.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    try {
      const v = JSON.parse(localStorage.getItem('wl_view') || 'null');
      if (v && v.symbol && v.view && v.view !== 'list') {
        if (v.view === 'chart') loadChart(v.symbol, v.exchange || 'NSE');
        else if (v.view === 'options') { setSelectedItem({ symbol: v.symbol, exchange: v.exchange || 'NSE' }); loadOptionChain(v.symbol, v.exchange || 'NSE'); }
        else { setSelectedItem({ symbol: v.symbol, exchange: v.exchange || 'NSE' }); setActiveView(v.view); }
      }
    } catch (e) { /* nothing saved */ }
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!restoredRef.current) return;
    try {
      localStorage.setItem('wl_view', JSON.stringify(selectedItem && activeView !== 'list'
        ? { symbol: selectedItem.symbol, exchange: selectedItem.exchange, view: activeView } : { view: 'list' }));
    } catch (e) { /* storage blocked */ }
  }, [selectedItem, activeView]);

  const handleOptionOrder = async (opt, side) => {
    await placeManual(opt.symbol, opt.exchange || 'NSE', side, Math.max(1, lots), opt.lot_size || 1, opt.name || opt.symbol, opt.ltp || opt.price || opt.last_price);
  };

  const handleStockOrder = async (item, side) => {
    placeManual(item.symbol, item.exchange || 'NSE', side, 1, 1, item.symbol, item.ltp || item.price || item.last_price);
  };

  const getBestPrice = (sym, ex) => {
    const key = `${ex}:${sym}`;
    const priceData = prices[key] || prices[sym] || {};
    return {
      ltp: priceData.ltp ?? priceData.price ?? 0,
      open: priceData.open ?? 0,
      high: priceData.high || priceData.ltp || priceData.price || 0,
      low: priceData.low || priceData.ltp || priceData.price || 0,
      volume: priceData.volume ?? 0,
      change: priceData.change || 0,
      changePercent: priceData.changePercent || 0
    };
  };

  const getTechnicals = (sym, ex) => technicals[`${ex}:${sym}`];
  const signalColor = { BUY: '#00ff88', SELL: '#ff4444', HOLD: '#888' };

  const ceOptions = optionChain.filter(o => o.option_type === 'CE');
  const peOptions = optionChain.filter(o => o.option_type === 'PE');

  const isAtm = (opt) => {
    if (!underlyingPrice) return false;
    const strikes = [...new Set(optionChain.map(o => o.strike))].sort((a, b) => a - b);
    const step = strikes.length > 1 ? Math.min(...strikes.slice(1).map((s, i) => s - strikes[i])) : 50;
    return Math.abs(opt.strike - underlyingPrice) <= step / 2 + 1;
  };

  return (
    <div className="watchlist-page">
      <h2 style={{ color: '#00d9ff', marginBottom: '1rem' }}>Watchlist</h2>
      {message && <div className={`message ${message.type}`}>{message.text}</div>}
      {orderAsk && <OrderConfirm ask={orderAsk} onClose={() => setOrderAsk(null)}
        onResult={(t, m) => showMessage(t, m, t === 'success' ? 6000 : 10000)} />}
      <PendingOrders onResult={(t, m) => showMessage(t, m, t === 'success' ? 6000 : 10000)} />

      {/* AI PICKS — stock options are not auto-traded; place the AI's pick yourself (paper or live) */}
      {(!picksAuto || aiPicks.length > 0) && (
      <div className="card full-width" style={{ border: '1px solid #00d9ff', marginBottom: '0.8rem' }}>
        <div className="card-header" style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="card-title">AI picks — stock options (you place them)</span>
          <span style={{ color: '#888', fontSize: '0.75rem' }}>passed every entry check · goes to YOUR account in your mode (Paper or Live)</span>
          <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#888' }}>Lots:{' '}
            <input type="number" min="1" value={pickLots} onChange={e => setPickLots(Math.max(1, parseInt(e.target.value, 10) || 1))} style={{ width: '56px' }} />
          </span>
        </div>
        {aiPicks.length === 0 ? (
          <div style={{ color: '#888', fontSize: '0.82rem' }}>No pick yet today — a pick appears here when a stock signal passes every check (index trades are still automatic).</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
              <thead><tr style={{ color: '#888', textAlign: 'left' }}>
                <th>Time</th><th>Pick</th><th>Premium @ pick</th><th>1 lot</th><th>Suggested stop</th><th>Why</th><th></th>
              </tr></thead>
              <tbody>
                {aiPicks.map(p => (
                  <tr key={p.id} style={{ borderTop: '1px solid #333' }}>
                    <td>{p.time}</td>
                    <td><b style={{ color: p.option_type === 'CE' ? '#00ff88' : '#ff6b6b' }}>{p.trade_symbol}</b><div style={{ color: '#888' }}>{p.symbol} @ ₹{p.spot} · score {p.score}{p.ml != null ? ` · ML ${p.ml}%` : ''}</div></td>
                    <td>₹{p.premium}</td>
                    <td>₹{(+p.cost_1lot || 0).toLocaleString('en-IN')} <span style={{ color: '#888' }}>({p.lot_size})</span></td>
                    <td>{p.stop != null ? `₹${p.stop}` : '—'}<div style={{ color: '#888', fontSize: '0.7rem' }}>{p.plan}</div></td>
                    <td style={{ color: '#aaa', maxWidth: '260px' }}>{p.think || (p.reasons || []).join(', ')}</td>
                    <td><button className="btn btn-start" style={{ padding: '0.25rem 0.7rem' }}
                      onClick={() => placeManual(p.trade_symbol, p.exchange || 'NSE', 'BUY', pickLots, p.lot_size || 1, p.trade_symbol)}>Buy</button></td>
                  </tr>))}
              </tbody>
            </table>
          </div>)}
      </div>)}

      {/* Add Symbol — one search box, autosuggest covers equity, F&O stocks,
          indices, and MCX commodities together as you type */}
      <div className="card full-width wl-search-card">
        <div className="card-header">
          <span className="card-title">Add to Watchlist</span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <select value={exchange} onChange={e => setExchange(e.target.value)} style={{ padding: '0.5rem', background: '#1a1a2e', color: '#fff', border: '1px solid #333', borderRadius: '4px' }}>
            <option value="NSE">NSE</option>
            <option value="BSE">BSE</option>
            <option value="MCX">MCX</option>
          </select>
          <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
            <input
              type="text"
              placeholder="Type a symbol — stock, index, F&O, or commodity..."
              value={query}
              onChange={handleQueryChange}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onKeyDown={e => {
                if (e.key === 'Enter' && suggestions.length > 0) {
                  addToWatchlist(suggestions[0], suggestions[0].segment);
                }
              }}
              style={{ width: '100%' }}
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="suggestions-dropdown">
                {suggestions.map((sym, idx) => (
                  <div
                    key={idx}
                    className="suggestion-item"
                    // mousedown fires BEFORE the input's onBlur — using onClick here let
                    // onBlur's setTimeout hide the dropdown first, so the click silently
                    // never registered. preventDefault stops the input from blurring at
                    // all, so the click still lands normally right after.
                    onMouseDown={(e) => { e.preventDefault(); addToWatchlist(sym, sym.segment); }}
                  >
                    <span>
                      <span className="suggestion-symbol">{sym.symbol}</span>
                      {sym.name && sym.name !== sym.symbol && (
                        <span style={{ color: '#666', fontSize: '0.7rem', marginLeft: '0.4rem' }}>{sym.name}</span>
                      )}
                    </span>
                    <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.65rem', color: segmentColor[sym.segment] || '#888' }}>
                        {segmentLabel[sym.segment] || sym.segment}
                      </span>
                      <span className="suggestion-exchange">{sym.exchange}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button className={`btn ${activeView === 'list' ? 'btn-start' : 'btn-paper'}`} onClick={() => setActiveView('list')}>
          Watchlist ({watchlist.length})
        </button>
        {selectedItem && (
          <>
            <button className={`btn ${activeView === 'chart' ? 'btn-start' : 'btn-paper'}`} onClick={() => loadChart(selectedItem.symbol, selectedItem.exchange)}>
              Chart: {selectedItem.symbol}
            </button>
            <button className={`btn ${activeView === 'options' ? 'btn-start' : 'btn-paper'}`} onClick={() => loadOptionChain(selectedItem.symbol, selectedItem.exchange)}>
              Options: {selectedItem.symbol}
            </button>
            <button className={`btn ${activeView === 'technicals' ? 'btn-start' : 'btn-paper'}`} onClick={() => setActiveView('technicals')}>
              Technicals: {selectedItem.symbol}
            </button>
          </>
        )}
      </div>

      {/* Watchlist View */}
      {activeView === 'list' && (
        <div className="card full-width">
          {watchlist.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
              <p>Your watchlist is empty</p>
              <p style={{ fontSize: '0.8rem' }}>Search and add symbols above to track them</p>
            </div>
          ) : (
            <div>
              <table className="positions-table watchlist-table">
                <thead>
                  <tr>
                    <th style={{ width: '16%' }}>Symbol</th>
                    <th style={{ width: '8%' }}>LTP</th>
                    <th style={{ width: '7%' }}>Open</th>
                    <th style={{ width: '7%' }}>High</th>
                    <th style={{ width: '7%' }}>Low</th>
                    <th style={{ width: '7%' }}>Close</th>
                    <th style={{ width: '11%' }}>Change</th>
                    <th style={{ width: '6%' }}>Vol</th>
                    <th style={{ width: '19%' }}>Technicals</th>
                    <th style={{ width: '12%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {watchlist.map((item, idx) => {
                    const p = getBestPrice(item.symbol, item.exchange);
                    const seg = inferSegment(item);
                    const fmtVol = (v) => !v ? '—' : v >= 1e7 ? (v / 1e7).toFixed(1) + 'Cr' : v >= 1e5 ? (v / 1e5).toFixed(1) + 'L' : v >= 1e3 ? (v / 1e3).toFixed(1) + 'K' : String(v);
                    return (
                      <tr key={idx} className="watchlist-row" onClick={() => loadChart(item.symbol, item.exchange)}>
                        <td>
                          <strong style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.symbol}</strong>
                          <span style={{ fontSize: '0.62rem', color: segmentColor[seg] }}>{segmentLabel[seg] || seg}</span>
                          <span style={{ fontSize: '0.62rem', color: '#777', marginLeft: '0.35rem' }}>{item.exchange}</span>
                        </td>
                        <td className="ltp-cell">{p.ltp ? p.ltp.toFixed(2) : '—'}</td>
                        <td style={{ color: '#aaa' }}>{p.open ? p.open.toFixed(2) : '—'}</td>
                        <td style={{ color: '#00ff88' }}>{p.high ? p.high.toFixed(2) : '—'}</td>
                        <td style={{ color: '#ff4444' }}>{p.low ? p.low.toFixed(2) : '—'}</td>
                        <td style={{ color: '#aaa' }}>{p.prevClose ? p.prevClose.toFixed(2) : '—'}</td>
                        <td className={p.change >= 0 ? 'positive' : 'negative'}>
                          {p.change >= 0 ? '+' : ''}{p.change?.toFixed(2) || '0.00'}
                          <span style={{ display: 'block', fontSize: '0.68rem', opacity: 0.85 }}>({p.change >= 0 ? '+' : ''}{p.changePercent?.toFixed(2) || '0.00'}%)</span>
                        </td>
                        <td style={{ color: '#888' }}>{fmtVol(Number(p.volume))}</td>
                        <td className="tech-cell">
                          {(() => {
                            const t = getTechnicals(item.symbol, item.exchange);
                            if (!t) return <span style={{ color: '#555' }}>…</span>;
                            if (!t.available) return <span style={{ color: '#555' }}>{t.reason || 'N/A'}</span>;
                            return (
                              <span>
                                <span style={{ color: signalColor[t.signal] || '#888', fontWeight: 600 }}>{t.signal}</span>
                                <span style={{ color: '#666' }}> {t.confidence}% · RSI {t.rsi} · </span>
                                <span style={{ color: t.trend === 'UPTREND' ? '#00ff88' : t.trend === 'DOWNTREND' ? '#ff4444' : '#888' }}>{t.trend}</span>
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-start wl-btn" onClick={(e) => { e.stopPropagation(); loadChart(item.symbol, item.exchange); }}>Chart</button>
                          <button className="btn btn-paper wl-btn" title="Open in a chart window (resizable, all indicators, Renko, bid/ask)"
                                  onClick={(e) => { e.stopPropagation(); openChart(item.symbol, item.exchange); }}>📈 Window</button>
                          <button className="btn btn-paper wl-btn" onClick={(e) => { e.stopPropagation(); loadOptionChain(item.symbol, item.exchange); }}>Opt</button>
                          {seg === 'equity' && (
                            <>
                              <button className="btn btn-start wl-btn" style={{ background: '#00c46a' }} onClick={(e) => { e.stopPropagation(); handleStockOrder(item, 'BUY'); }}>Buy</button>
                              {my.holds(item.symbol) && <button className="btn btn-stop wl-btn" onClick={(e) => { e.stopPropagation(); handleStockOrder(item, 'SELL'); }}>Sell</button>}
                            </>
                          )}
                          <button className="btn btn-stop wl-btn" onClick={(e) => { e.stopPropagation(); removeFromWatchlist(item.symbol, item.exchange); }}>✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Chart View — real candles, or an honest message if unavailable */}
      {activeView === 'chart' && selectedItem && (() => {
        const cp = getBestPrice(selectedItem.symbol, selectedItem.exchange);
        return (
        <div className="card full-width">
          <div className="card-header">
            <span className="card-title">{selectedItem.symbol} — Chart</span>
          </div>
          {/* Summary stats at the TOP of the chart card, directly under the header */}
          <div style={{ display: 'flex', gap: '2rem', padding: '0.5rem 0.5rem 1rem', borderBottom: '1px solid #333', flexWrap: 'wrap' }}>
            <div>
              <span style={{ color: '#888', fontSize: '0.8rem' }}>LTP</span>
              <p className="ltp-large">₹{cp.ltp ? cp.ltp.toFixed(2) : '—'}</p>
            </div>
            <div>
              <span style={{ color: '#888', fontSize: '0.8rem' }}>Open</span>
              <p style={{ color: '#aaa', fontSize: '1.1rem' }}>₹{cp.open ? cp.open.toFixed(2) : '—'}</p>
            </div>
            <div>
              <span style={{ color: '#888', fontSize: '0.8rem' }}>High</span>
              <p style={{ color: '#00ff88', fontSize: '1.1rem' }}>₹{cp.high ? cp.high.toFixed(2) : '—'}</p>
            </div>
            <div>
              <span style={{ color: '#888', fontSize: '0.8rem' }}>Low</span>
              <p style={{ color: '#ff4444', fontSize: '1.1rem' }}>₹{cp.low ? cp.low.toFixed(2) : '—'}</p>
            </div>
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            <ChartView symbol={selectedItem.symbol} exchange={selectedItem.exchange || 'NSE'} />
          </div>
          {bigPlayerMtf && bigPlayerMtf.tf && (
            <div style={{ marginTop: '0.75rem' }}>
              <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.35rem' }}>
                Big player across timeframes — overall <strong style={{ color: bigPlayerMtf.side === 'BUYING' ? '#ffd700' : bigPlayerMtf.side === 'SELLING' ? '#c084fc' : '#888' }}>{bigPlayerMtf.side}</strong>
                <span style={{ color: '#555' }}> (higher timeframes count more; display only)</span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {['5m', '15m', '1h', 'Day'].map(k => {
                  const t = bigPlayerMtf.tf[k] || {};
                  const col = t.side === 'BUYING' ? '#ffd700' : t.side === 'SELLING' ? '#c084fc' : '#666';
                  return (
                    <div key={k} style={{ background: '#0a0a0f', border: `1px solid ${col}`, borderRadius: 6, padding: '0.4rem 0.6rem', flex: '1 1 140px', minWidth: 0 }}>
                      <div style={{ fontSize: '0.7rem', color: '#888' }}>{k}</div>
                      <div style={{ color: col, fontWeight: 600, fontSize: '0.85rem' }}>{t.side || '—'}</div>
                      <div style={{ color: '#aaa', fontSize: '0.65rem' }}>{t.last || ''}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        );
      })()}

      {/* Technicals View — full indicator panel: RSI, MACD, ADX, ROC, Stochastic,
          EMA, VWAP, Bollinger, ATR, volume, slope — computed from real candles */}
      {activeView === 'technicals' && selectedItem && (() => {
        const t = getTechnicals(selectedItem.symbol, selectedItem.exchange);
        const stat = (label, value, color) => (
          <div style={{ background: '#0a0a0f', borderRadius: '8px', padding: '0.75rem 1rem', minWidth: '130px' }}>
            <div style={{ color: '#888', fontSize: '0.7rem', textTransform: 'uppercase' }}>{label}</div>
            <div style={{ color: color || '#fff', fontSize: '1.2rem', fontWeight: 600 }}>{value}</div>
          </div>
        );
        return (
          <div className="card full-width">
            <div className="card-header">
              <span className="card-title">{selectedItem.symbol} — Technicals</span>
              <span style={{ color: '#00d9ff' }}>LTP: ₹{getBestPrice(selectedItem.symbol, selectedItem.exchange).ltp?.toFixed(2) || '—'}</span>
            </div>
            {!t ? (
              <p style={{ color: '#888', padding: '1rem' }}>Loading…</p>
            ) : !t.available ? (
              <p style={{ color: '#ffb020', padding: '1rem' }}>{t.reason || 'Not available'}</p>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  {stat('AI Signal', `${t.signal} ${t.confidence}%`, signalColor[t.signal])}
                  {stat('Score', t.score, t.score > 0 ? '#00ff88' : t.score < 0 ? '#ff4444' : '#888')}
                  {stat('Trend', t.trend, t.trend === 'UPTREND' ? '#00ff88' : t.trend === 'DOWNTREND' ? '#ff4444' : '#888')}
                  {stat('Trend Strength', t.trend_strength)}
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {stat('RSI (14)', t.rsi, t.rsi >= 70 ? '#ff4444' : t.rsi <= 30 ? '#00ff88' : '#fff')}
                  {stat('MACD', t.macd?.toFixed(2))}
                  {stat('MACD Signal', t.macd_signal?.toFixed(2))}
                  {stat('MACD Histogram', t.macd_hist?.toFixed(2), t.macd_hist > 0 ? '#00ff88' : t.macd_hist < 0 ? '#ff4444' : '#888')}
                  {stat('ADX (14)', t.adx, t.adx >= 25 ? '#00d9ff' : '#888')}
                  {stat('ROC (10)', `${t.roc}%`, t.roc > 0 ? '#00ff88' : t.roc < 0 ? '#ff4444' : '#888')}
                  {stat('Stochastic %K', t.stoch_k, t.stoch_k >= 80 ? '#ff4444' : t.stoch_k <= 20 ? '#00ff88' : '#fff')}
                  {stat('EMA 9 / 21', `${t.ema_9?.toFixed(1)} / ${t.ema_21?.toFixed(1)}`, t.ema_9 > t.ema_21 ? '#00ff88' : '#ff4444')}
                  {stat('VWAP', `₹${t.vwap?.toFixed(2)}`, t.vwap_position_pct >= 0 ? '#00ff88' : '#ff4444')}
                  {stat('vs VWAP', `${t.vwap_position_pct >= 0 ? '+' : ''}${t.vwap_position_pct}%`, t.vwap_position_pct >= 0 ? '#00ff88' : '#ff4444')}
                  {stat('Bollinger Position', `${t.bb_position_pct}%`, t.bb_position_pct >= 80 ? '#ff4444' : t.bb_position_pct <= 20 ? '#00ff88' : '#fff')}
                  {stat('ATR (14)', t.atr?.toFixed(2))}
                  {stat('Volume Ratio', `${t.volume_ratio}x`, t.volume_ratio > 1.5 ? '#00d9ff' : '#888')}
                  {stat('Slope (10)', t.slope_10?.toFixed(3), t.slope_10 > 0 ? '#00ff88' : t.slope_10 < 0 ? '#ff4444' : '#888')}
                </div>
                <p style={{ color: '#555', fontSize: '0.7rem', marginTop: '1rem' }}>
                  Computed from real 5-min candles, refreshes every 15s (cached 60s server-side).
                </p>
              </>
            )}
          </div>
        );
      })()}

      {/* Option Chain View — real listed contracts, LTP, lot size */}
      {activeView === 'options' && selectedItem && (
        <>
          <div className="card full-width">
            <div className="card-header">
              <span className="card-title">{selectedItem.symbol} Options</span>
              <span style={{ color: '#00d9ff' }}>Underlying: ₹{underlyingPrice.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              {selectedItem.exchange?.toUpperCase() === 'MCX' && mcxOptionExpiries.length > 0 ? (
                <span style={{ color: '#888', fontSize: '0.8rem' }}>
                  Expiry:{' '}
                  <select
                    value={selectedExpiry}
                    onChange={e => handleExpiryChange(e.target.value)}
                    style={{ background: '#1a1a1a', color: '#fff', border: '1px solid #333', borderRadius: '4px', padding: '0.15rem 0.4rem' }}
                  >
                    {mcxOptionExpiries.map(exp => (
                      <option key={exp} value={exp}>{exp}</option>
                    ))}
                  </select>
                </span>
              ) : (
                chainExpiry && <span style={{ color: '#888', fontSize: '0.8rem' }}>Expiry: <strong style={{ color: '#fff' }}>{chainExpiry}</strong></span>
              )}
              {!chainIsReal && (
                <span style={{ color: '#ffb020', fontSize: '0.75rem', border: '1px solid #ffb020', borderRadius: '4px', padding: '0 6px' }}>
                  Estimated strikes — no listed contracts found, LTP unavailable
                </span>
              )}
              <label style={{ color: '#888', fontSize: '0.8rem' }}>Lots:</label>
              <input type="number" value={lots} onChange={e => setLots(Math.max(1, parseInt(e.target.value) || 1))} min="1" style={{ width: '70px' }} />
              <button className="btn btn-paper" onClick={() => loadOptionChain(selectedItem.symbol, selectedItem.exchange, selectedExpiry)}>Refresh</button>
              <span style={{ color: '#555', fontSize: '0.7rem' }}>Auto-refreshes every 4s</span>
            </div>
          </div>

          {selectedItem.exchange?.toUpperCase() === 'MCX' && mcxFutures.length > 0 && (
            <div className="card full-width">
              <div className="card-header">
                <span className="card-title" style={{ color: '#00d9ff' }}>Futures Contracts</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="positions-table">
                  <thead><tr><th>Symbol</th><th>Expiry</th><th>LTP</th><th>Open</th><th>High</th><th>Low</th><th>Lot</th><th>Action</th></tr></thead>
                  <tbody>
                    {mcxFutures.map((f, idx) => (
                      <tr key={idx} style={{ background: f.expiry === chainExpiry ? 'rgba(0,217,255,0.08)' : 'transparent' }}>
                        <td><strong>{f.symbol}</strong></td>
                        <td style={{ color: '#aaa', fontSize: '0.8rem' }}>{f.expiry}</td>
                        <td style={{ color: '#00d9ff' }}>{f.ltp ? `₹${f.ltp.toFixed(2)}` : '—'}</td>
                        <td style={{ color: '#aaa', fontSize: '0.8rem' }}>{f.open ? f.open.toFixed(2) : '—'}</td>
                        <td style={{ color: '#00ff88', fontSize: '0.8rem' }}>{f.high ? f.high.toFixed(2) : '—'}</td>
                        <td style={{ color: '#ff4444', fontSize: '0.8rem' }}>{f.low ? f.low.toFixed(2) : '—'}</td>
                        <td style={{ color: '#888', fontSize: '0.8rem' }}>{f.lot_size || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-start" style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }} onClick={() => handleOptionOrder({ ...f, exchange: 'MCX' }, 'BUY')}>BUY</button>
                          {my.holds(f.symbol) && <button className="btn btn-stop" style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem', marginLeft: '0.2rem' }} onClick={() => handleOptionOrder({ ...f, exchange: 'MCX' }, 'SELL')}>SELL</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {loadingChain && optionChain.length === 0 ? (
            <div className="card full-width" style={{ textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: '#888' }}>Loading option chain…</p>
            </div>
          ) : optionChain.length === 0 ? (
            <div className="card full-width" style={{ textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: '#888' }}>No options available for {selectedItem.symbol}</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="card">
                <div className="card-header">
                  <span className="card-title" style={{ color: '#00ff88' }}>CALL (CE)</span>
                </div>
                <div style={{ maxHeight: '450px', overflowY: 'auto', overflowX: 'auto' }}>
                  <table className="positions-table">
                    <thead><tr><th>Strike</th><th>LTP</th><th>Open</th><th>High</th><th>Low</th><th>Lot</th><th>Action</th></tr></thead>
                    <tbody>
                      {ceOptions.map((opt, idx) => (
                        <tr key={idx} style={{ background: isAtm(opt) ? 'rgba(0,255,136,0.08)' : 'transparent' }}>
                          <td>
                            <strong>{opt.strike}</strong>
                            {isAtm(opt) && <span style={{ color: '#00ff88', fontSize: '0.65rem', marginLeft: '0.3rem' }}>ATM</span>}
                          </td>
                          <td style={{ color: '#00d9ff' }}>{opt.ltp ? `₹${opt.ltp.toFixed(2)}` : '—'}</td>
                          <td style={{ color: '#aaa', fontSize: '0.8rem' }}>{opt.open ? opt.open.toFixed(2) : '—'}</td>
                          <td style={{ color: '#00ff88', fontSize: '0.8rem' }}>{opt.high ? opt.high.toFixed(2) : '—'}</td>
                          <td style={{ color: '#ff4444', fontSize: '0.8rem' }}>{opt.low ? opt.low.toFixed(2) : '—'}</td>
                          <td style={{ color: '#888', fontSize: '0.8rem' }}>{opt.lot_size || '—'}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <button className="btn btn-start" style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }} onClick={() => handleOptionOrder(opt, 'BUY')}>BUY</button>
                            {my.holds(opt.symbol) && <button className="btn btn-stop" style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem', marginLeft: '0.2rem' }} onClick={() => handleOptionOrder(opt, 'SELL')}>SELL</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="card">
                <div className="card-header">
                  <span className="card-title" style={{ color: '#ff4444' }}>PUT (PE)</span>
                </div>
                <div style={{ maxHeight: '450px', overflowY: 'auto', overflowX: 'auto' }}>
                  <table className="positions-table">
                    <thead><tr><th>Strike</th><th>LTP</th><th>Open</th><th>High</th><th>Low</th><th>Lot</th><th>Action</th></tr></thead>
                    <tbody>
                      {peOptions.map((opt, idx) => (
                        <tr key={idx} style={{ background: isAtm(opt) ? 'rgba(255,68,68,0.08)' : 'transparent' }}>
                          <td>
                            <strong>{opt.strike}</strong>
                            {isAtm(opt) && <span style={{ color: '#ff4444', fontSize: '0.65rem', marginLeft: '0.3rem' }}>ATM</span>}
                          </td>
                          <td style={{ color: '#00d9ff' }}>{opt.ltp ? `₹${opt.ltp.toFixed(2)}` : '—'}</td>
                          <td style={{ color: '#aaa', fontSize: '0.8rem' }}>{opt.open ? opt.open.toFixed(2) : '—'}</td>
                          <td style={{ color: '#00ff88', fontSize: '0.8rem' }}>{opt.high ? opt.high.toFixed(2) : '—'}</td>
                          <td style={{ color: '#ff4444', fontSize: '0.8rem' }}>{opt.low ? opt.low.toFixed(2) : '—'}</td>
                          <td style={{ color: '#888', fontSize: '0.8rem' }}>{opt.lot_size || '—'}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <button className="btn btn-start" style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }} onClick={() => handleOptionOrder(opt, 'BUY')}>BUY</button>
                            {my.holds(opt.symbol) && <button className="btn btn-stop" style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem', marginLeft: '0.2rem' }} onClick={() => handleOptionOrder(opt, 'SELL')}>SELL</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Watchlist;
